import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["getIsUngated", "onChange", "onBlur", "error", "field", "hideExtraInputs", "getTimezoneOptions", "inputUse", "value", "condition", "useOnePanel"];
import { useState, useEffect } from 'react';
import I18n from 'I18n';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { getTransferableProps } from './filterInputProps';
import { fromSimpleDateToMoment, fromMomentToSimpleDate } from 'customer-data-filters-ui/utils/momentSimpleDateConversion';
import { hasRemoveTimezoneForDates } from '../../permissions/gates';
import TimezoneToggle from './TimezoneToggle';
import UpdateDateFilterWithTimezonesNotification from './UpdateDateFilterWithTimezonesNotification';
import { DATE_TIME } from 'customer-data-objects/property/PropertyTypes';
import { getIsUTC as getIsUTCFromUtils } from '../../utils/timezoneUtils';
import EqualTimePoint from 'customer-data-filters-ui/filterQueryFormat/operator/EqualTimePoint';
import BeforeTimePoint from 'customer-data-filters-ui/filterQueryFormat/operator/BeforeTimePoint';
import AfterTimePoint from 'customer-data-filters-ui/filterQueryFormat/operator/AfterTimePoint';
import getConditionId from '../../utils/getConditionId';
import TimezoneDropdown from './TimezoneDropdown';
import { FlexDateInputWithTimezoneWrapper } from '../../constants/inlineLayout';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FilterOperatorDateInputMoment = props => {
  const {
      getIsUngated,
      onChange,
      onBlur,
      error,
      field,
      hideExtraInputs,
      getTimezoneOptions,
      inputUse,
      value,
      condition,
      useOnePanel
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [showTimezoneToggle, setShowTimezoneToggle] = useState(false);
  const {
    hasTimezones
  } = condition;
  const isDateTimeOperator = field.get('type') === DATE_TIME;
  const getIsUTC = () => getIsUTCFromUtils(condition, getIsUngated);
  useEffect(() => {
    if (hasRemoveTimezoneForDates(getIsUngated) && !isDateTimeOperator && !hasTimezones && (condition.constructor === EqualTimePoint || condition.constructor === BeforeTimePoint || condition.constructor === AfterTimePoint)) {
      setShowTimezoneToggle(false);
    } else {
      setShowTimezoneToggle(!hideExtraInputs);
    }
  }, [condition.constructor, getIsUngated, hasTimezones, isDateTimeOperator, hideExtraInputs]);
  const handleChange = evt => {
    if (hasRemoveTimezoneForDates(getIsUngated) && !hasTimezones) {
      // @ts-expect-error we added types to `fromSimpleDateToMoment` after this was written
      const newValue = fromSimpleDateToMoment(evt.target.value, false);
      onChange(syntheticEvent(newValue));
    } else {
      // @ts-expect-error we added types to `fromSimpleDateToMoment` after this was written
      const newValue = fromSimpleDateToMoment(evt.target.value, getIsUTC());
      onChange(syntheticEvent(newValue));
    }
  };
  const handleUpdateClick = () => {
    if (!value) {
      return;
    }
    setShowTimezoneToggle(false);
    const newValue = I18n.moment.portalTz(value.format('YYYY-MM-DD'));
    onChange(syntheticEvent(newValue), {
      hasClickedUpdate: true
    });
  };
  const handleTimezoneChange = newUseUTC => {
    if (!value) {
      return;
    }
    const newValue = newUseUTC ? I18n.moment.utc(value.format('YYYY-MM-DD')) : I18n.moment.portalTz(value.format('YYYY-MM-DD'));
    onChange(syntheticEvent(newValue));
  };
  const handleOpenChange = evt => {
    if (!evt.target.value && onBlur) {
      onBlur();
    }
  };
  const isError = error.get('error');
  const errorMessage = error.get('message');
  const transferableProps = getTransferableProps(rest);
  const {
    ['data-selenium-test']: dataSeleniumTest
  } = transferableProps;
  // @ts-expect-error allows us to avoid spreading props to UIComponents
  const disabled = transferableProps.disabled;
  // @ts-expect-error allows us to avoid spreading props to UIComponents
  const readOnly = transferableProps.readOnly;
  const InputWrapper = useOnePanel ? FlexDateInputWithTimezoneWrapper : 'div';
  const TimezoneSelector = useOnePanel ? TimezoneDropdown : TimezoneToggle;
  return /*#__PURE__*/_jsx(FormControl, {
    "aria-label": I18n.text('customerDataFiltersUI.customerDataFilters.FilterOperatorInput.ariaLabel'),
    error: isError,
    validationMessage: isError ? errorMessage : null,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(InputWrapper, {
        children: [/*#__PURE__*/_jsx(UIDateInput, {
          "data-selenium-test": dataSeleniumTest,
          disabled: disabled,
          readOnly: readOnly,
          use: inputUse,
          onChange: handleChange,
          onOpenChange: handleOpenChange,
          value: fromMomentToSimpleDate(value)
        }), showTimezoneToggle && /*#__PURE__*/_jsx(TimezoneSelector, {
          conditionId: getConditionId(condition),
          timezoneOptions: getTimezoneOptions(field),
          fieldType: field.get('type'),
          useUTC: getIsUTC(),
          onChange: handleTimezoneChange,
          getIsUngated: getIsUngated
        })]
      }), showTimezoneToggle && hasRemoveTimezoneForDates(getIsUngated) && !isDateTimeOperator && /*#__PURE__*/_jsx(UpdateDateFilterWithTimezonesNotification, {
        onUpdateClick: handleUpdateClick,
        useUTC: getIsUTC()
      })]
    })
  });
};
export default FilterOperatorDateInputMoment;